import React from 'react';
import { BarChart, Trash2, Layers, ArrowRightLeft } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных инвестиций
 */
const ComparisonSidebar = ({ 
  savedInvestments, 
  deleteSavedInvestment, 
  loadSavedInvestment,
  cryptocurrencyTypes,
  investmentCurrencies
}) => {
  // Если нет сохраненных инвестиций
  if (!savedInvestments || savedInvestments.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение инвестиций
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты инвестиций для сравнения.
        </p>
        <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg text-xs text-indigo-700 dark:text-indigo-300">
          Сохраняйте различные сценарии инвестирования, чтобы сравнивать их и выбрать оптимальный вариант.
        </div>
      </div>
    );
  }

  // Получение символа валюты
  const getCurrencySymbol = (currencyType) => {
    const currency = investmentCurrencies.find(c => c.id === currencyType);
    return currency ? currency.symbol : '₽';
  };

  // Получение названия криптовалюты
  const getCryptoName = (cryptoType) => {
    const crypto = cryptocurrencyTypes.find(c => c.id === cryptoType);
    return crypto ? crypto.name.split(' ')[0] : cryptoType;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение инвестиций
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedInvestments.length} {savedInvestments.length === 1 ? 'вариант' : savedInvestments.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedInvestments.map((investment) => {
          const currencySymbol = getCurrencySymbol(investment.formData?.investmentCurrency || 'rub');
          const cryptoName = getCryptoName(investment.formData?.cryptocurrencyType || 'bitcoin');
          
          return (
            <div 
              key={investment.id} 
              className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {investment.name}
                </h4>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Layers className="w-4 h-4" />}
                    onClick={() => loadSavedInvestment(investment.id)}
                    title="Загрузить параметры"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 className="w-4 h-4 text-red-500" />}
                    onClick={() => deleteSavedInvestment(investment.id)}
                    title="Удалить"
                  />
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between mb-1">
                  <span>Криптовалюта:</span>
                  <span className="font-medium">
                    {cryptoName}
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>Сумма:</span>
                  <span className="font-medium">
                    {formatNumber(investment.formData?.initialInvestment || 0, 0)} {currencySymbol}
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>Срок:</span>
                  <span>
                    {investment.formData?.investmentPeriod || 0} мес.
                  </span>
                </div>
                <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-200 dark:border-gray-600">
                  <span>Ожидаемый рост:</span>
                  <span className="font-medium text-indigo-600 dark:text-indigo-400">
                    {investment.formData?.expectedGrowthRate || 0}% в месяц
                  </span>
                </div>
                
                {investment.results && (
                  <>
                    <div className="flex justify-between mb-1">
                      <span>Итоговая стоимость:</span>
                      <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                        {formatNumber(investment.results.finalInvestment || 0, 0)} {currencySymbol}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Доходность:</span>
                      <span className={`font-semibold ${
                        (investment.results.returnPercentage || 0) >= 0 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {formatNumber(investment.results.returnPercentage || 0, 1)}%
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {savedInvestments.length >= 2 && (
        <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg flex items-start gap-2">
          <ArrowRightLeft className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-xs font-medium text-green-700 dark:text-green-300 mb-1">
              Сравнение вариантов
            </h4>
            <p className="text-xs text-green-600 dark:text-green-400">
              {compareInvestments(savedInvestments)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Функция для генерации текста сравнения инвестиций
const compareInvestments = (investments) => {
  if (investments.length < 2 || !investments[0].results || !investments[1].results) {
    return "Загрузите инвестиции для сравнения их эффективности.";
  }

  // Сортировка по доходности в процентах
  const sortedInvestments = [...investments]
    .filter(inv => inv.results)
    .sort((a, b) => (b.results.returnPercentage || 0) - (a.results.returnPercentage || 0));

  const best = sortedInvestments[0];
  const worst = sortedInvestments[sortedInvestments.length - 1];

  if (best.id === worst.id || !best.results || !worst.results) {
    return "Инвестиции имеют схожую эффективность.";
  }

  const bestName = best.name.length > 20 ? best.name.substring(0, 20) + '...' : best.name;
  const worstName = worst.name.length > 20 ? worst.name.substring(0, 20) + '...' : worst.name;

  const difference = (best.results.returnPercentage || 0) - (worst.results.returnPercentage || 0);

  if (difference <= 5) {
    return `Инвестиции "${bestName}" и "${worstName}" имеют близкую доходность с разницей ${formatNumber(difference, 1)}%.`;
  }

  return `Инвестиция "${bestName}" показывает доходность на ${formatNumber(difference, 1)}% выше, чем "${worstName}".`;
};

export default ComparisonSidebar;